import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/Staff.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/Staff.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { Staff } from '../../models/Entities/Staff/Staff';
import {
    CreateStaffCommand,
    StaffFilter,
    UpdateStaffCommand
} from '../../models/Entities/Staff/StaffCommands';
import { IPagedResult } from '../../models/HttpPagedResult';
import { PaginationState } from '../../models/PaginationState';

export type KnownAction =
    | Actions.Staff.StaffGetAllRequestAction
    | Actions.Staff.StaffGetAllSuccessAction
    | Actions.Staff.StaffGetAllFailureAction
    | Actions.Staff.StaffGetByIdRequestAction
    | Actions.Staff.StaffGetByIdSuccessAction
    | Actions.Staff.StaffGetByIdFailureAction
    | Actions.Staff.StaffCreateRequestAction
    | Actions.Staff.StaffCreateSuccessAction
    | Actions.Staff.StaffCreateFailureAction
    | Actions.Staff.StaffUpdateRequestAction
    | Actions.Staff.StaffUpdateSuccessAction
    | Actions.Staff.StaffUpdateFailureAction
    | Actions.Staff.StaffDeleteRequestAction
    | Actions.Staff.StaffDeleteSuccessAction
    | Actions.Staff.StaffDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

/**
 * Paginación de la respuesta, con valores propios cuando la API no los manda.
 *
 * Se leen sólo los cuatro números. La API manda además `firstPage`, `lastPage`,
 * `nextPage` y `previousPage`, pero como DIRECCIONES, y `PaginationState` los
 * declara numéricos: copiarlos metería una URL en un campo que dice ser un número
 * y el compilador no lo vería. Nadie los usa —el control de paginación se maneja
 * con el número de página y el total— así que se descartan en vez de mentir.
 */
const ReadPagination = (
    response: IPagedResult<Staff[]>,
    filter: StaffFilter | undefined
): PaginationState => ({
    pageNumber: response.pageNumber ?? filter?.PageNumber ?? 1,
    pageSize: response.pageSize ?? filter?.PageSize ?? 20,
    totalPages: response.totalPages ?? 0,
    totalRecords: response.totalRecords ?? 0
});

export const actionCreators = {
    /**
     * Trae una página del staff.
     *
     * Siempre pide, aunque ya haya una lista cargada: acá el filtro y el corte los
     * resuelve el servidor, así que lo que está en memoria es una página concreta
     * de una búsqueda concreta y no sirve para responder la siguiente.
     */
    GetAllStaff: (filter?: StaffFilter): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.STAFF_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.STAFF_GETALL_REQUEST, filter });

        Services.GetStaff(token, filter)
            .then((response: IPagedResult<Staff[]>) => {
                dispatch({
                    type: ActionEvents.STAFF_GETALL_SUCCESS,
                    payload: response.data ?? [],
                    pagination: ReadPagination(response, filter)
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.STAFF_GETALL_FAILURE, error });
            });
    },

    GetStaffById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.STAFF_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.STAFF_GETBYID_REQUEST, payload: id });

        Services.GetStaffById(token, id)
            .then((response: Staff) => {
                dispatch({ type: ActionEvents.STAFF_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.STAFF_GETBYID_FAILURE, error });
            });
    },

    /**
     * Alta. La API devuelve sólo el id, así que la fila nueva no se puede armar
     * con lo que responde: se vuelve a pedir la página que se está mirando.
     */
    CreateStaff: (payload: CreateStaffCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.STAFF_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.STAFF_CREATE_REQUEST, payload });

        Services.CreateStaff(token, payload)
            .then((createdId: number) => {
                dispatch({ type: ActionEvents.STAFF_CREATE_SUCCESS, payload: createdId });
                dispatch(actionCreators.GetAllStaff(state.Staff?.filter) as any);
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.STAFF_CREATE_FAILURE, error });
            });
    },

    /**
     * Modificación. Lo que cambia son datos de la persona, que el listado casi no
     * muestra, pero el nombre sí: se recarga la página para que la tabla no quede
     * con el anterior.
     */
    UpdateStaff: (payload: UpdateStaffCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.STAFF_UPDATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.STAFF_UPDATE_REQUEST, payload });

        Services.UpdateStaff(token, payload)
            .then((updatedId: number) => {
                dispatch({ type: ActionEvents.STAFF_UPDATE_SUCCESS, payload: updatedId });
                dispatch(actionCreators.GetAllStaff(state.Staff?.filter) as any);
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.STAFF_UPDATE_FAILURE, error });
            });
    },

    /**
     * Baja. Se saca del listado en el reducer y además se recarga: borrar un
     * registro de una página corre a los que siguen, y sin volver a pedir la
     * tabla queda con un lugar vacío que no le corresponde.
     */
    DeleteStaff: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.STAFF_DELETE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.STAFF_DELETE_REQUEST, payload: id });

        Services.DeleteStaff(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.STAFF_DELETE_SUCCESS, payload: id });
                dispatch(actionCreators.GetAllStaff(state.Staff?.filter) as any);
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.STAFF_DELETE_FAILURE, error });
            });
    }
};
